import { processError, logError, PaymentError, ERROR_TYPES } from '../errors/errorHandler'
import axios from 'axios'

// Base URL da API (deve vir do .env)
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api'

// Timeout para requisições (10 segundos)
const REQUEST_TIMEOUT = 10000

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

/**
 * Validar cupom de desconto
 * @param {string} couponCode - Código do cupom
 * @param {number} ticketId - ID do ingresso (opcional)
 * @returns {Promise<Object>} Dados do cupom validado
 */
export const validateCoupon = async (couponCode, ticketId = null) => {
  try {
    if (!couponCode || couponCode.trim() === '') {
      throw new PaymentError(
        'Código do cupom é obrigatório',
        'missing_coupon_code',
        ERROR_TYPES.VALIDATION,
        { field: 'coupon_code' }
      )
    }

    // Backend endpoint POST /cupom retorna o objeto Cupom direto (id, cupom,
    // discount, description, status, used, used_at) ou 404 se não encontrar.
    // Aceita o campo `cupom` no body (não `code`).
    const response = await api.post('/cupom', {
      cupom: couponCode.toUpperCase().trim(),
      ticket_id: ticketId
    })

    const data = response.data || {}
    const alreadyUsed = data.used === 1 || data.used === true
    const isActive = data.status === 1 || data.status === true

    return {
      id: data.id || null,
      valid: !!data.id && isActive && !alreadyUsed,
      discount: parseFloat(data.discount) || 0,
      code: data.cupom || data.code || couponCode,
      description: data.description || '',
      type: data.type || 'bulk_single_use',
      max_uses: data.max_uses || null,
      uses_count: alreadyUsed ? 1 : 0,
      remaining_uses: alreadyUsed ? 0 : null,
      message: alreadyUsed
        ? 'Cupom já utilizado.'
        : (!isActive ? 'Cupom inativo.' : 'Cupom válido.')
    }
  } catch (error) {
    const processedError = processError(error, {
      operation: 'validateCoupon',
      couponCode
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Calcular desconto do cupom sobre um valor
 * @param {number} originalAmount - Valor original
 * @param {number} discountPercent - Percentual de desconto (0-100)
 * @returns {Object} Valores calculados
 */
export const calculateDiscount = (originalAmount, discountPercent) => {
  const amount = parseFloat(originalAmount)
  const discount = parseFloat(discountPercent)

  if (isNaN(amount) || amount <= 0) {
    return {
      original: 0,
      discount: 0,
      discountAmount: 0,
      final: 0
    }
  }

  if (isNaN(discount) || discount < 0 || discount > 100) {
    return {
      original: amount,
      discount: 0,
      discountAmount: 0,
      final: amount
    }
  }

  const discountAmount = (amount * discount) / 100
  const finalAmount = amount - discountAmount

  return {
    original: amount,
    discount: discount,
    discountAmount: discountAmount,
    final: finalAmount
  }
}

/**
 * Aplicar cupom no pedido
 * @param {string} couponCode - Código do cupom
 * @param {number} ticketId - ID do ingresso
 * @param {number} quantity - Quantidade de ingressos
 * @param {number} unitPrice - Preço unitário do ingresso
 * @returns {Promise<Object>} Resumo do pedido com desconto aplicado
 */
export const applyCoupon = async (couponCode, ticketId, quantity, unitPrice) => {
  try {
    // Validar cupom
    const coupon = await validateCoupon(couponCode, ticketId)

    if (!coupon.valid) {
      throw new PaymentError(
        coupon.message || 'Cupom inválido',
        'invalid_coupon',
        ERROR_TYPES.VALIDATION,
        { couponCode }
      )
    }

    // Calcular valores
    const subtotal = parseFloat(unitPrice) * parseInt(quantity)
    const discount = calculateDiscount(subtotal, coupon.discount)

    return {
      coupon: {
        code: coupon.code,
        description: coupon.description,
        discount: coupon.discount,
        type: coupon.type,
        remaining_uses: coupon.remaining_uses
      },
      order: {
        ticket_id: ticketId,
        quantity: quantity,
        unit_price: parseFloat(unitPrice),
        subtotal: discount.original,
        discount_percent: discount.discount,
        discount_amount: discount.discountAmount,
        total: discount.final
      }
    }
  } catch (error) {
    const processedError = processError(error, {
      operation: 'applyCoupon',
      couponCode,
      ticketId,
      quantity
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Remover cupom do pedido (recalcular sem desconto)
 * @param {number} quantity - Quantidade de ingressos
 * @param {number} unitPrice - Preço unitário do ingresso
 * @returns {Object} Resumo do pedido sem desconto
 */
export const removeCoupon = (quantity, unitPrice) => {
  const subtotal = parseFloat(unitPrice) * parseInt(quantity)

  return {
    coupon: null,
    order: {
      quantity: quantity,
      unit_price: parseFloat(unitPrice),
      subtotal: subtotal,
      discount_percent: 0,
      discount_amount: 0,
      total: subtotal
    }
  }
}

/**
 * Formatar percentual de desconto para exibição
 * @param {number} discount - Percentual de desconto
 * @returns {string} Desconto formatado
 */
export const formatDiscount = (discount) => {
  const value = parseFloat(discount)

  if (isNaN(value) || value <= 0) {
    return '0%'
  }

  return `${value.toFixed(0)}%`
}

/**
 * Verificar se cupom ainda tem usos disponíveis
 * @param {Object} coupon - Objeto do cupom
 * @returns {boolean} Se ainda pode ser usado
 */
export const hasUsesAvailable = (coupon) => {
  if (!coupon) return false

  // Cupons de uso múltiplo
  if (coupon.type === 'single_multi_use') {
    if (coupon.max_uses === null || coupon.max_uses === undefined) {
      return true // Sem limite
    }

    const remaining = parseInt(coupon.max_uses) - parseInt(coupon.uses_count || 0)
    return remaining > 0
  }

  // Cupons de uso único
  if (coupon.type === 'bulk_single_use') {
    return parseInt(coupon.uses_count || 0) === 0
  }

  return false
}

/**
 * Obter mensagem de status do cupom
 * @param {Object} coupon - Objeto do cupom
 * @returns {string} Mensagem de status
 */
export const getCouponStatusMessage = (coupon) => {
  if (!coupon) return ''

  if (!hasUsesAvailable(coupon)) {
    return 'Cupom já foi utilizado'
  }

  if (coupon.type === 'single_multi_use' && coupon.max_uses) {
    const remaining = parseInt(coupon.max_uses) - parseInt(coupon.uses_count || 0)
    return `${remaining} uso(s) restante(s)`
  }

  return 'Cupom válido'
}
